import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { CuentasContratoService } from 'src/app/services/perfil-bancario/cuentas-contrato.service';

const COLOR_POR_DEFECTO = '#64748b';
const clave = (n: string) => String(n || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Etiquetas internas de una cuenta: chips con su color y, si se puede editar, autocompletar contra el catálogo
 * (GET /etiquetas?prefijo) con la opción «Crear "X"» (POST /etiquetas). Nunca escribe en la cuenta: avisa con
 * `agregar` y `quitar`, y el borrador lo guarda con el resto de los cambios.
 */
@Component({
  selector: 'app-cuentas-contrato-etiquetas',
  templateUrl: './cuentas-contrato-etiquetas.component.html'
})
export class CuentasContratoEtiquetasComponent implements OnDestroy {
  @Input() etiquetas: string[] = [];
  @Input() catalogo: any[] = [];
  @Input() editable = false;
  @Output() agregar = new EventEmitter<string>();
  @Output() quitar = new EventEmitter<string>();
  /** Una etiqueta nueva del catálogo, para que el padre la sume a su lista. */
  @Output() creada = new EventEmitter<any>();

  abierto = false;
  texto = '';
  sugerencias: any[] = [];
  buscando = false;
  creando = false;
  error: string | null = null;
  private espera: any = null;
  private busquedaActual = 0;

  constructor(private servicio: CuentasContratoService) {}

  ngOnDestroy(): void { clearTimeout(this.espera); }

  colorDe(nombre: string): string {
    return this.catalogo.find((e) => clave(e.nombre) === clave(nombre))?.color || COLOR_POR_DEFECTO;
  }

  /** Estilo del chip: el color de la etiqueta como texto y borde, y un fondo apenas teñido. */
  estilo(nombre: string): { [k: string]: string } {
    const c = this.colorDe(nombre);
    return { color: c, borderColor: `${c}66`, backgroundColor: `${c}1a` };
  }

  abrir(): void {
    this.abierto = true;
    this.texto = '';
    this.error = null;
    this.buscar();
  }

  cerrar(): void {
    clearTimeout(this.espera);
    this.abierto = false;
    this.texto = '';
    this.sugerencias = [];
    this.error = null;
  }

  /** Cada tecla pide las sugerencias al catálogo, con una pequeña espera para no pedir de más. */
  escribir(valor: string): void {
    this.texto = valor;
    this.error = null;
    clearTimeout(this.espera);
    this.espera = setTimeout(() => this.buscar(), 200);
  }

  async buscar(): Promise<void> {
    const mia = ++this.busquedaActual;
    this.buscando = true;
    try {
      const r = await this.servicio.getEtiquetas(this.texto.trim(), 8);
      if (mia === this.busquedaActual) this.sugerencias = (r?.data || []).filter((e: any) => !this.yaLaTiene(e.nombre));
    } catch (error) {
      if (mia === this.busquedaActual) this.sugerencias = [];
    } finally {
      if (mia === this.busquedaActual) this.buscando = false;
    }
  }

  yaLaTiene(nombre: string): boolean {
    return this.etiquetas.some((e) => clave(e) === clave(nombre));
  }

  /** «Crear "X"» solo aparece si lo escrito no es ya una etiqueta del catálogo. */
  get puedeCrear(): boolean {
    const t = this.texto.trim();
    return !!t && !this.sugerencias.some((s) => clave(s.nombre) === clave(t)) && !this.yaLaTiene(t);
  }

  elegir(nombre: string): void {
    this.agregar.emit(nombre);
    this.cerrar();
  }

  async crear(): Promise<void> {
    const nombre = this.texto.trim();
    if (!nombre || this.creando) return;
    this.creando = true;
    this.error = null;
    try {
      const r = await this.servicio.crearEtiqueta(nombre);
      const etiqueta = r?.data;
      if (etiqueta) {
        this.creada.emit(etiqueta);
        this.agregar.emit(etiqueta.nombre);
        this.cerrar();
      }
    } catch (error: any) {
      this.error = error?.error?.message || 'No se pudo crear la etiqueta. Inténtalo de nuevo.';
    } finally {
      this.creando = false;
    }
  }

  /** Enter: elige la primera sugerencia o crea la que escribió. Escape: cierra. */
  teclas(e: KeyboardEvent): void {
    if (e.key === 'Escape') { this.cerrar(); return; }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const exacta = this.sugerencias.find((s) => clave(s.nombre) === clave(this.texto));
    if (exacta) this.elegir(exacta.nombre);
    else if (this.puedeCrear) this.crear();
    else if (this.sugerencias.length && !this.texto.trim()) this.elegir(this.sugerencias[0].nombre);
  }
}
